import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import './TermsModal.css';
import api from '../services/api';

const TermsModal = ({ show, onAccept, onLogout }) => {
  const [termsData, setTermsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  useEffect(() => {
    if (show) {
      fetchCurrentTerms();
    }
  }, [show]);

  const fetchCurrentTerms = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/terms/current');
      
      // Get user data from localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Replace placeholders in content
      let content = data.content;
      const displayName = userData.full_name || userData.username || '[Username]';
      content = content.replace(/{{username}}/g, displayName);
      content = content.replace(/{{full_name}}/g, userData.full_name || '[Full Name]');
      content = content.replace(/{{date}}/g, new Date().toLocaleDateString('sk-SK'));
      
      setTermsData({ ...data, content });
      setLoading(false);
      
      // Check if content is short enough that no scrolling is needed
      setTimeout(() => {
        const bodyElement = document.querySelector('.terms-modal-body');
        if (bodyElement) {
          const needsScroll = bodyElement.scrollHeight > bodyElement.clientHeight + 10;
          if (!needsScroll) {
            console.log('📄 Content is short, auto-enabling accept button');
            setScrolledToBottom(true);
          }
        }
      }, 100);
    } catch (err) {
      console.error('Error fetching terms:', err);
      setError('Failed to load terms and conditions');
      setLoading(false);
    }
  };

  const handleScroll = (e) => {
    const element = e.target;
    const isBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
    if (isBottom && !scrolledToBottom) {
      setScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    if (!termsData) return;

    try {
      setAccepting(true);
      
      // Get client info
      const ipAddress = await fetch('https://api.ipify.org?format=json')
        .then(res => res.json())
        .then(data => data.ip)
        .catch(() => 'unknown');
      
      const userAgent = navigator.userAgent;
      
      const { data: result } = await api.post('/terms/accept', {
        termsId: termsData.id,
        ipAddress,
        userAgent
      });
      console.log('✅ Terms accepted:', result);
      
      // Update user data in localStorage
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      userData.terms_accepted_version = result.version;
      localStorage.setItem('user', JSON.stringify(userData));
      
      if (onAccept) {
        onAccept();
      }
    } catch (err) {
      console.error('Error accepting terms:', err);
      setError('Failed to accept terms. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    if (onLogout) {
      onLogout();
    }
  };

  if (!show) return null;

  return (
    <div className="terms-modal-overlay">
      <div className="terms-modal">
        <div className="terms-modal-header">
          <h2>📜 Terms and Conditions</h2>
          <p className="terms-version">Version {termsData?.version || '1.0'}</p>
        </div>

        <div className="terms-modal-body" onScroll={handleScroll}>
          {loading && (
            <div className="terms-loading">
              <div className="spinner"></div>
              <p>Loading terms and conditions...</p>
            </div>
          )}

          {error && (
            <div className="terms-error">
              <p>{error}</p>
              <button onClick={fetchCurrentTerms} className="btn-retry">
                Retry
              </button>
            </div>
          )}

          {!loading && !error && termsData && (
            <div className="terms-content">
              <ReactMarkdown>{termsData.content}</ReactMarkdown>
            </div>
          )}

          {!scrolledToBottom && !loading && !error && (
            <div className="scroll-indicator">
              <p>⬇️ Please scroll to the bottom to continue</p>
            </div>
          )}
        </div>

        <div className="terms-modal-footer">
          <p className="terms-notice">
            ⚠️ You must accept these terms to continue using the application.
          </p>
          
          <div className="terms-actions">
            <button
              onClick={handleLogout}
              className="btn-logout"
              disabled={accepting}
            >
              ❌ Logout
            </button>
            
            <button
              onClick={handleAccept}
              className="btn-accept"
              disabled={!scrolledToBottom || accepting || loading || error}
            >
              {accepting ? (
                <>
                  <span className="spinner-small"></span>
                  Accepting...
                </>
              ) : (
                <>✅ I Accept</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
